import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid,
  Typography,
  MenuItem,
  IconButton,
  InputAdornment,
  ToggleButton,
  ToggleButtonGroup,
  Divider,
  Radio,
  RadioGroup,
  FormControlLabel,
} from "@mui/material";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import CloseIcon from "@mui/icons-material/Close";
import {
  useCreatePromotionMutation,
  useGetSyncedCategoriesAndMfrsMutation,
  useGetSyncedProductNumbersMutation,
} from "../../../../redux/reducer/features/Product/promotionSlice";
import { useGetAvailablePriceTypesQuery } from "../../../../redux/reducer/features/Integration/DmsSetupSlice";
import {
  DiscountMethod,
  PricingType,
  ProductType,
} from "../../../../models/features/Integration/DmsSetup";
import { MultiSelectWithCheckbox } from "./MultiSelectAutocomplete";
import { usePriceOptions } from "../../../../services/utilities/utility";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import SnackBarPopup from "../../../app/SnackBarPopup";
import {
  SubscriptionFeatureName,
  useHasSubscriptionFeature,
} from "../../../../services/featurePermissions/subscriptionFeatures";
import { useSelector } from "react-redux";
import { AppState } from "../../../../redux/reducer/rootReducer";
const discountMethodOptions = [
  { value: DiscountMethod.PercentDiscount, label: "% Discount" },
  { value: DiscountMethod.FlatAmountOff, label: "Flat $ Off" },
  { value: DiscountMethod.SetPrice, label: "Set Price" },
];

const PromotionSchema = Yup.object().shape({
  name: Yup.string().trim().required("Promotion name is required"),
  startDate: Yup.string().required("Start date is required"),
  endDate: Yup.string().required("End date is required"),
  basePrice: Yup.string().required("Base price level is required"),
  discountMethod: Yup.string().required(),
  discount: Yup.number()
    .typeError("Value must be a number")
    .when("discountMethod", (discountMethod, schema) => {
      switch (discountMethod) {
        case DiscountMethod.PercentDiscount:
          return schema
            .required("Percentage is required")
            .moreThan(0, "Enter a value from 1 to 99")
            .max(99, "Enter a value from 1 to 99")
            .integer("Please enter a number (e.g. 10, 25, 50)");

        case DiscountMethod.FlatAmountOff:
          return schema
            .required("Amount is required")
            .moreThan(0, "Amount must be greater than 0");

        case DiscountMethod.SetPrice:
          return schema
            .required("Price is required")
            .moreThan(0, "Price must be greater than 0");

        default:
          return schema.required("Value is required");
      }
    }),
});

const initialValues = {
  name: "",
  startDate: "",
  endDate: "",
  basePrice: "Price",
  discountMethod: DiscountMethod.PercentDiscount,
  discount: 0,
};

interface CreatePromotionDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

dayjs.extend(utc);

export default function CreatePromotionDialog({
  open,
  onClose,
  onSuccess,
}: CreatePromotionDialogProps) {
  const priceOptions = usePriceOptions();

  const hasWholegoods = useHasSubscriptionFeature(
    SubscriptionFeatureName.Wholegoods,
  );
  const hasParts = useHasSubscriptionFeature(SubscriptionFeatureName.Parts);
  const productTypes = useSelector(
    (state: AppState) => state.authReducer.dmsSetup?.productTypes,
  );
  const promotionProductTypes = React.useMemo(() => {
    const types: ProductType[] = [];
    if (hasWholegoods) types.push(ProductType.Wholegoods);
    if (hasParts && productTypes?.some((n) => n === "Parts"))
      types.push(ProductType.Parts);
    return types;
  }, [hasWholegoods, hasParts]);

  const [getSyncedCategoriesAndMfrs, getSyncedCategoriesAndMfrsResult] =
    useGetSyncedCategoriesAndMfrsMutation();
  const [getSyncedProductNumbers, getSyncedProductNumbersResult] =
    useGetSyncedProductNumbersMutation();
  const [createPromotion, createPromotionResult] = useCreatePromotionMutation();

  const [productType, setProductType] = React.useState<ProductType>(
    promotionProductTypes[0] ?? ProductType.Wholegoods,
  );

  // Keep the selected product type in sync with what's actually allowed by
  // the subscription (e.g. if Wholegoods is removed, fall back to Parts).
  React.useEffect(() => {
    if (
      promotionProductTypes.length > 0 &&
      !promotionProductTypes.includes(productType)
    ) {
      setProductType(promotionProductTypes[0]);
    }
  }, [promotionProductTypes, productType]);

  const [selectedCategories, setSelectedCategories] = React.useState<string[]>(
    [],
  );
  const [selectedBrands, setSelectedBrands] = React.useState<string[]>([]);
  const [selectedProducts, setSelectedProducts] = React.useState<string[]>([]);

  const [categoryOptions, setCategoryOptions] = React.useState<string[]>([]);
  const [brandOptions, setBrandOptions] = React.useState<string[]>([]);
  const [productOptions, setProductOptions] = React.useState<string[]>([]);

  const [showAlert, setShowAlert] = React.useState(false);
  const [alertMessage, setAlertMessage] = React.useState("");
  const [alertSeverity, setAlertSeverity] = React.useState<"success" | "error">(
    "success",
  );

  const { data: availablePriceTypesData, isLoading: isPriceTypesLoading } =
    useGetAvailablePriceTypesQuery(
      { producType: productType },
      { skip: !open },
    );

  const filteredPriceOptions = React.useMemo(() => {
    if (!availablePriceTypesData?.entityList?.length) return [];
    const allowedTypes = availablePriceTypesData.entityList
      .map((name: string) => PricingType[name as keyof typeof PricingType])
      .filter((val): val is PricingType => val !== undefined);
    return priceOptions.filter((o) => allowedTypes.includes(o.type));
  }, [availablePriceTypesData, priceOptions]);

  const handleProductTypeChange = (type: ProductType) => {
    setProductType(type);
    setSelectedCategories([]);
    setSelectedBrands([]);
    setSelectedProducts([]);
    setProductOptions([]);
  };

  const resetAllState = () => {
    setProductType(promotionProductTypes[0] ?? ProductType.Wholegoods);
    setSelectedCategories([]);
    setSelectedBrands([]);
    setSelectedProducts([]);
    setCategoryOptions([]);
    setBrandOptions([]);
    setProductOptions([]);
  };

  React.useEffect(() => {
    if (open) getSyncedCategoriesAndMfrs({ productType });
  }, [open, productType, getSyncedCategoriesAndMfrs]);

  React.useEffect(() => {
    if (
      getSyncedCategoriesAndMfrsResult.isSuccess &&
      getSyncedCategoriesAndMfrsResult.data
    ) {
      const d = getSyncedCategoriesAndMfrsResult.data.entity;
      setCategoryOptions(d?.productLines ?? []);
      setBrandOptions(d?.manufacturers ?? []);
    }
  }, [getSyncedCategoriesAndMfrsResult]);

  React.useEffect(() => {
    if (!open) return;
    const hasSelection =
      selectedCategories.length > 0 || selectedBrands.length > 0;

    if (!hasSelection) {
      setProductOptions([]);
      setSelectedProducts([]);
      return;
    }

    const timer = setTimeout(() => {
      getSyncedProductNumbers({
        productLines: selectedCategories,
        manufacturers: selectedBrands,
        productType,
      });
    }, 500);
    return () => clearTimeout(timer);
  }, [
    selectedCategories,
    selectedBrands,
    productType,
    open,
    getSyncedProductNumbers,
  ]);

  React.useEffect(() => {
    if (
      getSyncedProductNumbersResult.isSuccess &&
      getSyncedProductNumbersResult.data
    ) {
      const newOptions =
        getSyncedProductNumbersResult.data.entity?.productNumbers ?? [];
      setProductOptions(newOptions);
      setSelectedProducts((prev) => prev.filter((p) => newOptions.includes(p)));
    }
  }, [getSyncedProductNumbersResult]);

  const handleSave = (values: typeof initialValues) => {
    const requestData = {
      name: values.name,
      startDate: dayjs(values.startDate)
        .startOf("day")
        .format("YYYY-MM-DDTHH:mm:ss"),
      endDate: dayjs(values.endDate).endOf("day").format("YYYY-MM-DDTHH:mm:ss"),
      productType: ProductType[productType],
      basePriceType: values.basePrice,
      discountMethod: values.discountMethod,
      discountValue: values.discount,
      category: selectedCategories,
      brand: selectedBrands,
      productNumber: selectedProducts,
    };
    createPromotion(requestData).then((res: any) => {
      if (res?.data?.status === 200) {
        setAlertSeverity("success");
        setAlertMessage(res.data.message || "Promotion created successfully.");
        setShowAlert(true);
        resetAllState();
        onSuccess();
      } else {
        setAlertSeverity("error");
        setAlertMessage(
          res?.data?.message ||
            res?.error?.data?.message ||
            "Failed to create promotion.",
        );
        setShowAlert(true);
      }
    });
  };

  return (
    <>
      <Dialog
        open={open}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
        onClose={(_event, reason) => {
          if (reason !== "backdropClick") onClose();
        }}
      >
        <Formik
          initialValues={initialValues}
          validationSchema={PromotionSchema}
          onSubmit={handleSave}
          enableReinitialize
        >
          {({
            errors,
            touched,
            values,
            setFieldValue,
            resetForm,
            setFieldTouched,
            setFieldError,
            validateField,
          }) => (
            <Form noValidate>
              {/* ── 1. Title ── */}
              <DialogTitle
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  pb: 1,
                }}
              >
                <Typography variant="h6" fontWeight={600}>
                  Create Promotion
                </Typography>
                <IconButton
                  aria-label="close"
                  onClick={() => {
                    resetForm();
                    onClose();
                    resetAllState();
                  }}
                  size="small"
                >
                  <CloseIcon />
                </IconButton>
              </DialogTitle>

              <Divider />

              <DialogContent sx={{ p: 3 }}>
                <Grid container spacing={2}>
                  {/* ── 2. Promotion Name ── */}
                  <Grid item xs={12}>
                    <TextField
                      label="Promotion Name"
                      name="name"
                      variant="outlined"
                      size="small"
                      fullWidth
                      placeholder="e.g. Summer Sale"
                      value={values.name}
                      onChange={(e) => setFieldValue("name", e.target.value)}
                      error={touched.name && !!errors.name}
                      helperText={touched.name && errors.name}
                    />
                  </Grid>

                  {/* ── 3. Start Date ── */}
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Start Date"
                      name="startDate"
                      type="date"
                      variant="outlined"
                      size="small"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      inputProps={{
                        min: dayjs().format("YYYY-MM-DD"),
                      }}
                      value={values.startDate}
                      onChange={(e) =>
                        setFieldValue("startDate", e.target.value)
                      }
                      error={touched.startDate && !!errors.startDate}
                      helperText={
                        touched.startDate && (errors.startDate as string)
                      }
                      aria-describedby={
                        touched.startDate && errors.startDate
                          ? "startDate-error"
                          : undefined
                      }
                    />
                  </Grid>

                  {/* ── 4. End Date ── */}
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="End Date"
                      name="endDate"
                      type="date"
                      variant="outlined"
                      size="small"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      inputProps={{
                        min: values.startDate || dayjs().format("YYYY-MM-DD"),
                      }}
                      value={values.endDate}
                      onChange={(e) => setFieldValue("endDate", e.target.value)}
                      error={touched.endDate && !!errors.endDate}
                      helperText={touched.endDate && (errors.endDate as string)}
                      aria-describedby={
                        touched.endDate && errors.endDate
                          ? "endDate-error"
                          : undefined
                      }
                    />
                  </Grid>

                  {/* ── 5. Product Type ── */}
                  <Grid item xs={12}>
                    <Typography
                      variant="body2"
                      fontWeight={600}
                      color="text.secondary"
                      sx={{ mb: 1 }}
                    >
                      Product Type
                    </Typography>
                    <ToggleButtonGroup
                      exclusive
                      value={productType}
                      onChange={(_, val) => {
                        if (val !== null) handleProductTypeChange(val);
                      }}
                      size="small"
                      aria-label="Product Type"
                    >
                      {promotionProductTypes.map((type) => (
                        <ToggleButton
                          key={type}
                          value={type}
                          sx={{
                            px: 3,
                            textTransform: "none",
                            fontWeight: 500,
                          }}
                        >
                          {ProductType[type]}
                        </ToggleButton>
                      ))}
                    </ToggleButtonGroup>
                  </Grid>

                  {/* ── 6. Category ── */}
                  <Grid item xs={12}>
                    <MultiSelectWithCheckbox
                      options={categoryOptions}
                      value={selectedCategories}
                      onChange={setSelectedCategories}
                      label="Category"
                    />
                  </Grid>

                  {/* ── 7. Brand ── */}
                  <Grid item xs={12}>
                    <MultiSelectWithCheckbox
                      options={brandOptions}
                      value={selectedBrands}
                      onChange={setSelectedBrands}
                      label="Brand"
                    />
                  </Grid>

                  {/* ── 9. Product # ── */}
                  <Grid item xs={12}>
                    <MultiSelectWithCheckbox
                      options={productOptions}
                      value={selectedProducts}
                      onChange={setSelectedProducts}
                      label="Product #"
                    />
                  </Grid>

                  {/* ── Section divider between filters and pricing ── */}
                  <Grid item xs={12}>
                    <Divider sx={{ mt: 1 }} />
                  </Grid>

                  {/* ── 13. Base Price Level ── */}
                  <Grid item xs={12}>
                    <Field
                      as={TextField}
                      select
                      label="Base Price Level"
                      name="basePrice"
                      variant="outlined"
                      size="small"
                      fullWidth
                      disabled={isPriceTypesLoading}
                      helperText="Price level used to calculate discount"
                    >
                      {filteredPriceOptions.map((option) => (
                        <MenuItem
                          key={option.key}
                          value={PricingType[option.type]}
                        >
                          {option.label}
                        </MenuItem>
                      ))}
                    </Field>
                  </Grid>

                  {/* ── 14. Discount Method ── */}
                  <Grid item xs={12}>
                    <Typography
                      variant="body2"
                      fontWeight={600}
                      color="text.secondary"
                      sx={{ mb: 0.5 }}
                    >
                      Discount Method
                    </Typography>
                    <RadioGroup
                      row
                      value={values.discountMethod}
                      onChange={(e) => {
                        setFieldValue("discountMethod", e.target.value);
                        setFieldValue("discount", "");

                        setFieldTouched("discount", false);
                        setFieldError("discount", undefined);

                        validateField("discount");
                      }}
                      aria-label="Discount Method"
                    >
                      {discountMethodOptions.map(({ value, label }) => (
                        <FormControlLabel
                          key={value}
                          value={value}
                          control={<Radio size="small" />}
                          label={
                            <Typography variant="body2">{label}</Typography>
                          }
                        />
                      ))}
                    </RadioGroup>
                  </Grid>

                  {/* ── 15. Discount Value ── */}
                  <Grid item xs={12}>
                    <TextField
                      label={
                        values.discountMethod === DiscountMethod.PercentDiscount
                          ? "Discount (%)"
                          : values.discountMethod ===
                              DiscountMethod.FlatAmountOff
                            ? "Amount ($)"
                            : "Set Price"
                      }
                      name="discount"
                      type="number"
                      variant="outlined"
                      size="small"
                      fullWidth
                      value={values.discount}
                      onChange={(e) =>
                        setFieldValue("discount", e.target.value)
                      }
                      error={touched.discount && !!errors.discount}
                      helperText={touched.discount && errors.discount}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            {values.discountMethod ===
                            DiscountMethod.PercentDiscount
                              ? "%"
                              : "$"}
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                </Grid>
              </DialogContent>

              <Divider />

              {/* ── 16. Action Buttons ── */}
              <DialogActions
                sx={{
                  px: 3,
                  py: 2,
                  gap: 1,
                  justifyContent: "flex-end",
                  flexDirection: { xs: "column", sm: "row" },
                }}
              >
                <Button
                  variant="outlined"
                  color="inherit"
                  fullWidth={false}
                  onClick={() => {
                    resetForm();
                    onClose();
                    resetAllState();
                  }}
                  sx={{ width: { xs: "100%", sm: "auto" } }}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={createPromotionResult.isLoading}
                  sx={{ width: { xs: "100%", sm: "auto" } }}
                >
                  Save Promotion
                </Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>
      <SnackBarPopup
        severity={alertSeverity}
        message={alertMessage}
        onClose={() => setShowAlert(false)}
        open={showAlert}
        autoHideDuration={5000}
      />
    </>
  );
}
